import React, { useState, useEffect, useCallback } from 'react';
import TabContainerHOC from './hocs/TabContainerHOC';
import Notification from '../global/Notification';
import StickySaveBar from '../global/StickySaveBar';
import { PageProps } from '../../types';
import { LinkIcon } from '../../assets/LinkIcon';
import BannerStylesCustomization from './components/LandscapeBanner/BannerStylesCustomization';
import BannerLivePreview from './components/LandscapeBanner/BannerLivePreview';
import ImageUploadBox from './components/ImageUploadBox';
import SliderInput from './components/SliderInput';
import { useImageUpload } from './hooks/useImageUpload';
import { useUnsavedChangesGuard } from './hooks/useUnsavedChangesGuard';
import Tooltip from '@mui/material/Tooltip';
import { getCustomizationSection, saveCustomizationSection } from '../../lib/customization/customizationService';

export interface BannerData {
    primaryText: string;
    secondaryText: string;
    buttonText: string;
    url: string;
    desktopSrc: string;
    mobileSrc: string;
    primaryTextColor: string;
    secondaryTextColor: string;
    buttonColor: string;
    buttonTextColor: string;
    bannerBackgroundColor: string;
    textAlignment: string;
    buttonAlignment: string;
    layout: 'column' | 'row';
    imageBrightness: number;
    openInNewTab: boolean;
}

const defaultBanner: BannerData = {
    primaryText: '',
    secondaryText: '',
    buttonText: '',
    url: '',
    desktopSrc: '',
    mobileSrc: '',
    primaryTextColor: '#000000',
    secondaryTextColor: '#000000',
    buttonColor: '#000000',
    buttonTextColor: '#ffffff',
    bannerBackgroundColor: '#ffffff',
    textAlignment: 'left',
    buttonAlignment: 'left',
    layout: 'column',
    imageBrightness: 100,
    openInNewTab: false,
};

export default function BannerLandscapePage({ model }: PageProps) {
    const [banner, setBanner] = useState<BannerData>(defaultBanner);
    const [originalData, setOriginalData] = useState<BannerData | null>(null);
    const [linkMode, setLinkMode] = useState<'banner' | 'button'>('banner');

    const [calling, setCalling] = useState(false);

    const [hasChanges, setHasChanges] = useState(false);

    const [notification, setNotification] = useState<{ message: string | null; type: 'success' | 'error' }>({
        message: null,
        type: 'success',
    });
    const [saveError, setSaveError] = useState<string | null>(null);


    const closeNotification = useCallback(() => setNotification({ message: null, type: 'success' }), []);
    const dismissSaveError = useCallback(() => setSaveError(null), []);

    const fetchBanner = async () => {
        setCalling(true);
        try {
            const data = await getCustomizationSection<any>(model.storeId, 'BannerLandscape');

            const apiData = data || {};
            const baseline: BannerData = {
                primaryText: apiData.primaryText || '',
                secondaryText: apiData.secondaryText || '',
                buttonText: apiData.buttonText || '',
                url: apiData.url || apiData.option1Url || '',
                desktopSrc: apiData.desktopSrc || '',
                mobileSrc: apiData.mobileSrc || '',
                primaryTextColor: apiData.primaryTextColor || '#000000',
                secondaryTextColor: apiData.secondaryTextColor || '#000000',
                buttonColor: apiData.buttonColor || '#000000',
                buttonTextColor: apiData.buttonTextColor || '#ffffff',
                bannerBackgroundColor: apiData.bannerBackgroundColor || '#ffffff',
                textAlignment: apiData.textAlignment || 'left',
                buttonAlignment: apiData.buttonAlignment || 'left',
                layout: apiData.layout === 'row' ? 'row' : 'column',
                imageBrightness: apiData.imageBrightness ?? 100,
                openInNewTab: apiData.openInNewTab ?? false,
            };

            setBanner(baseline);
            setOriginalData(baseline);
            setHasChanges(false);
        } catch (error) {
            console.error('Error fetching Banner Landscape:', error);
            setNotification({ message: 'Failed to load banner data. Please try again later.', type: 'error' });
        } finally {
            setCalling(false);
        }
    };

    useEffect(() => {
        if (model?.storeId) {
            fetchBanner();
        }
    }, [model?.storeId]);

    useEffect(() => {
        if (originalData) {
            setLinkMode(originalData.buttonText?.trim() ? 'button' : 'banner');
        }
    }, [originalData]);

    const checkForChanges = (newData: BannerData) => {
        if (!originalData) return false;
        return (
            newData.primaryText !== originalData.primaryText ||
            newData.secondaryText !== originalData.secondaryText ||
            newData.buttonText !== originalData.buttonText ||
            newData.url !== originalData.url ||
            newData.desktopSrc !== originalData.desktopSrc ||
            newData.mobileSrc !== originalData.mobileSrc ||
            newData.primaryTextColor !== originalData.primaryTextColor ||
            newData.secondaryTextColor !== originalData.secondaryTextColor ||
            newData.buttonColor !== originalData.buttonColor ||
            newData.buttonTextColor !== originalData.buttonTextColor ||
            newData.bannerBackgroundColor !== originalData.bannerBackgroundColor ||
            newData.textAlignment !== originalData.textAlignment ||
            newData.buttonAlignment !== originalData.buttonAlignment ||
            newData.layout !== originalData.layout ||
            newData.imageBrightness !== originalData.imageBrightness ||
            newData.openInNewTab !== originalData.openInNewTab
        );
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { id, type } = e.target;
        const value = type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
        const updatedBanner = { ...banner, [id]: value };
        setBanner(updatedBanner);
        setHasChanges(checkForChanges(updatedBanner));
    };

    const updateStyle = (key: keyof BannerData, value: string | number) => {
        const updatedBanner = { ...banner, [key]: value };
        setBanner(updatedBanner);
        setHasChanges(checkForChanges(updatedBanner));
    };

    const handleLinkModeChange = (mode: 'banner' | 'button') => {
        setLinkMode(mode);
        if (mode === 'banner') {
            const updated = { ...banner, buttonText: '' };
            setBanner(updated);
            setHasChanges(checkForChanges(updated));
        }
    };

    const {
        uploadingDesktop,
        uploadingMobile,
        handleImageUpload,
        handleRemoveImage
    } = useImageUpload({
        storeId: model.storeId,
        setNotification,
        onImageUpdate: updateStyle
    });

    const saveBanner = async (): Promise<boolean> => {
        const hasButtonText = banner.buttonText.trim().length > 0;
        const hasUrl = banner.url.trim().length > 0;
        if (linkMode === 'button' && hasButtonText !== hasUrl) {
            setSaveError('Please provide both button text and a URL.');
            return false;
        }

        if ((banner.desktopSrc && !banner.mobileSrc) || (!banner.desktopSrc && banner.mobileSrc)) {
            setSaveError('Please upload both desktop and mobile images.');
            return false;
        }

        setCalling(true);
        setSaveError(null);

        try {
            await saveCustomizationSection({
                storeId: model.storeId,
                metafield: 'BannerLandscape',
                section: 'bannerLandscape',
                payload: banner,
                lastValue: originalData,
            });

            setNotification({ message: 'Banner saved successfully!', type: 'success' });
            setOriginalData(banner);
            setHasChanges(false);
            return true;
        } catch (error) {
            console.error('Error saving banner:', error);
            setSaveError('Failed to save banner. Please try again.');
            return false;
        } finally {
            setCalling(false);
        }
    };

    const unsavedChangesModal = useUnsavedChangesGuard({ hasChanges, save: saveBanner });

    const handleCancel = () => {
        if (originalData) {
            setBanner(originalData);
            setLinkMode(originalData.buttonText?.trim() ? 'button' : 'banner');
            setHasChanges(false);
        }
        setSaveError(null);
    };

    return (
        <>
            <TabContainerHOC title="Landscape Banner" description={<Description />} containerClass="banner-landscape" loading={calling}>
                <div className="row title-inputs">
                    <div className="col-sm col-left">
                        <h2>Edit the Site Landscape Banner</h2>
                    </div>
                </div>

                <div className="banner-form-container">

                    {/* Text */}
                    <div className="bl-section-card">
                        <div className="bl-section-card-header">
                            <h4>Text</h4>
                        </div>
                        <div className="bl-section-card-body">
                            <div className="row">
                                <div className="col-sm col-left">
                                    <div className="field-group">
                                        <p className="field-label"><b>Add primary text</b></p>
                                        <p className="field-hint">Main heading text. Recommended 30 characters approximately.</p>
                                        <input
                                            id="primaryText"
                                            value={banner.primaryText}
                                            onChange={handleChange}
                                            disabled={calling}
                                            type="text"
                                            className="input-round form-control"
                                            placeholder="Type text."
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row mt-3">
                                <div className="col-sm col-left">
                                    <div className="field-group">
                                        <p className="field-label"><b>Add secondary text</b></p>
                                        <p className="field-hint">Secondary supporting text below the title. Recommended 50 characters approximately.</p>
                                        <textarea
                                            id="secondaryText"
                                            value={banner.secondaryText}
                                            onChange={handleChange}
                                            disabled={calling}
                                            rows={1}
                                            className="input-round form-control multi-line"
                                            placeholder="Type text. Press Enter for a new line."
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Link & Action */}
                    <div className="bl-section-card">
                        <div className="bl-section-card-header">
                            <h4>Link &amp; Action</h4>
                        </div>
                        <div className="bl-section-card-body">
                            <div className="field-group">
                                <p className="field-label"><b>Destination URL</b></p>
                                <p className="field-hint">The link users reach when clicking the banner.</p>
                                <div className="d-flex align-items-center">
                                    <div className="input-with-icon" style={{ flexGrow: 1, width: 'auto' }}>
                                        <LinkIcon />
                                        <input
                                            id="url"
                                            value={banner.url}
                                            onChange={handleChange}
                                            disabled={calling}
                                            type="text"
                                            className="input-round form-control"
                                            placeholder="https://"
                                        />
                                    </div>
                                    <div className="checkbox-side">
                                        <label>
                                            <span>New Tab</span>
                                            <input
                                                id="openInNewTab"
                                                type="checkbox"
                                                checked={banner.openInNewTab}
                                                onChange={handleChange}
                                                disabled={calling}
                                            />
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="field-group mt-3">
                                <p className="field-label"><b>Link behavior</b></p>
                                <p className="field-hint">Choose how users interact with the banner.</p>
                                <div className="bl-option-cards">
                                    <label className={`bl-option-card ${linkMode === 'banner' ? 'selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="linkMode"
                                            value="banner"
                                            checked={linkMode === 'banner'}
                                            onChange={() => handleLinkModeChange('banner')}
                                            disabled={calling}
                                        />
                                        <div className="bl-option-card-inner">
                                            <div className="bl-option-card-header-row">
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                                                    <line x1="8" y1="21" x2="16" y2="21" />
                                                    <line x1="12" y1="17" x2="12" y2="21" />
                                                </svg>
                                                <span className="bl-option-card-title">Entire banner clickable</span>
                                            </div>
                                            <span className="bl-option-card-desc">Clicking anywhere on the banner opens the link.</span>
                                        </div>
                                    </label>
                                    <label className={`bl-option-card ${linkMode === 'button' ? 'selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="linkMode"
                                            value="button"
                                            checked={linkMode === 'button'}
                                            onChange={() => handleLinkModeChange('button')}
                                            disabled={calling}
                                        />
                                        <div className="bl-option-card-inner">
                                            <div className="bl-option-card-header-row">
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="2" y="7" width="20" height="10" rx="2" />
                                                    <line x1="7" y1="12" x2="17" y2="12" />
                                                </svg>
                                                <span className="bl-option-card-title">Show a button</span>
                                            </div>
                                            <span className="bl-option-card-desc">Add a call-to-action button with custom text.</span>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            <Tooltip title={linkMode !== 'button' ? "Select 'Show a button' to enable this field." : ''} arrow placement="bottom">
                                <span className="bl-lockable-wrapper">
                                    <div className={`field-group mt-3${linkMode !== 'button' ? ' bl-section-locked' : ''}`}>
                                        <p className="field-label"><b>Button text</b></p>
                                        <input
                                            id="buttonText"
                                            value={banner.buttonText}
                                            onChange={handleChange}
                                            disabled={calling || linkMode !== 'button'}
                                            type="text"
                                            className="input-round form-control"
                                            placeholder="e.g. Shop Now"
                                        />
                                    </div>
                                </span>
                            </Tooltip>
                        </div>
                    </div>

                    {/* Background Images */}
                    <div className="bl-section-card">
                        <div className="bl-section-card-header">
                            <h4>Background Images <span className="bl-section-optional">(Optional)</span></h4>
                        </div>
                        <div className="bl-section-card-body">
                            <div className="row">
                                <div className="col-sm-8 col-left bl-upload-col">
                                    <ImageUploadBox
                                        id="desktopSrc"
                                        label="Image desktop"
                                        hint="Recommended size: 1400 × 215 px"
                                        src={banner.desktopSrc}
                                        uploading={uploadingDesktop}
                                        disabled={calling}
                                        boxClass="desktop-box"
                                        onUpload={(e) => handleImageUpload(e, 'desktopSrc')}
                                        onRemove={() => handleRemoveImage('desktopSrc')}
                                    />
                                </div>
                                <div className="col-sm-4 col-right bl-upload-col">
                                    <ImageUploadBox
                                        id="mobileSrc"
                                        label="Image mobile"
                                        hint="Recommended size: 420 × 260 px"
                                        src={banner.mobileSrc}
                                        uploading={uploadingMobile}
                                        disabled={calling}
                                        boxClass="mobile-box"
                                        onUpload={(e) => handleImageUpload(e, 'mobileSrc')}
                                        onRemove={() => handleRemoveImage('mobileSrc')}
                                    />
                                </div>
                            </div>
                            <div className="row mt-3">
                                <div className="col-12 col-left">
                                    <SliderInput
                                        label="Image Brightness"
                                        value={banner.imageBrightness}
                                        min={0}
                                        max={100}
                                        unit="%"
                                        onChange={(val) => updateStyle('imageBrightness', val)}
                                        disabled={calling}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <BannerStylesCustomization
                        primaryTextColor={banner.primaryTextColor}
                        setPrimaryTextColor={(val) => updateStyle('primaryTextColor', val)}
                        secondaryTextColor={banner.secondaryTextColor}
                        setSecondaryTextColor={(val) => updateStyle('secondaryTextColor', val)}
                        buttonColor={banner.buttonColor}
                        setButtonColor={(val) => updateStyle('buttonColor', val)}
                        buttonTextColor={banner.buttonTextColor}
                        setButtonTextColor={(val) => updateStyle('buttonTextColor', val)}
                        bannerBackgroundColor={banner.bannerBackgroundColor}
                        setBannerBackgroundColor={(val) => updateStyle('bannerBackgroundColor', val)}
                        textAlignment={banner.textAlignment}
                        setTextAlignment={(val) => updateStyle('textAlignment', val)}
                        buttonAlignment={banner.buttonAlignment}
                        setButtonAlignment={(val) => updateStyle('buttonAlignment', val)}
                        layout={banner.layout}
                        setLayout={(val) => updateStyle('layout', val)}
                        hasButton={linkMode === 'button'}
                        disabled={calling}
                    />

                </div>

                <hr className="section-divider my-4" />

                <BannerLivePreview bannerData={banner} linkMode={linkMode} />

                {unsavedChangesModal}
            </TabContainerHOC>
            <StickySaveBar
                visible={hasChanges}
                saving={calling}
                disabled={uploadingDesktop || uploadingMobile}
                onSave={saveBanner}
                onCancel={handleCancel}
                alignWithSelector=".leftVertical"
                errorMessage={saveError ?? undefined}
                onErrorDismiss={dismissSaveError}
            />
            <Notification message={notification.message} type={notification.type} onClose={closeNotification} />
        </>
    );
}

const Description = () => (
    <ul>
        <li>
            Provide a short text labeled as <b>Primary Text</b> and an additional block labeled as <b>Secondary Text</b>.
        </li>
        <li>
            Optionally, you can upload a <b>Background Image</b> or define a solid <b>Background Color</b> for your banner.
        </li>
        <li>
            Set a <b>Destination URL</b> and choose between making the entire banner clickable or showing a button with custom text.
        </li>
        <li>
            Use the style section to align elements and adjust the colors.
        </li>
    </ul>
);